import { useId, useState } from 'react';
import type * as React from 'react';
import { Link } from 'react-router';
import { ShieldAlert } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { DataTable, type Column } from '@/components/data-table';
import { CursorFooter } from '@/components/cursor-footer';
import { EmptyState } from '@/components/states';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import { FormField, Label, NativeSelect, Textarea } from '@/components/ui/field';
import { Badge, Mono } from '@/components/ui/primitives';
import {
  RESOURCES,
  type AdminReport,
  type ReportStatus,
  type ReportSubjectType,
} from '@/api/resources';
import { useAdminRequest, useCursorPage } from '@/hooks/use-cursor-page';
import { notify } from '@/lib/notify';
import { formatDateTime } from '@/lib/format';

const STATUSES: readonly ReportStatus[] = ['OPEN', 'REVIEWING', 'RESOLVED', 'DISMISSED'];
const SUBJECT_TYPES: readonly ReportSubjectType[] = ['USER', 'CHILD', 'WISHLIST_ITEM'];

const STATUS_TONE: Record<ReportStatus, 'caution' | 'info' | 'success' | 'neutral'> = {
  OPEN: 'caution',
  REVIEWING: 'info',
  RESOLVED: 'success',
  DISMISSED: 'neutral',
};

/**
 * The report queue (06 §2) — `SUPPORT` and `SUPER_ADMIN`.
 *
 * ⚠️ Roles are enforced server-side: `AdminModerationController` is decorated
 * `@AdminRoles('SUPER_ADMIN', 'SUPPORT')` and refuses everyone else with
 * `403 INSUFFICIENT_ROLE`. This screen agreeing with that is convenience.
 *
 * ⚠️ **No subject preview, deliberately.** A third of this queue has
 * `subjectType = CHILD`, and drawing those names in a list would be child data
 * shown with no stated reason and no audit row naming the child (§4.1). The row
 * carries the subject id and a link; the name, if a moderator genuinely needs
 * one, comes from the audited reveal on the user detail screen.
 *
 * ⚠️ Resolution is terminal. A second moderator working the same queue gets
 * `409 REPORT_ALREADY_RESOLVED` rather than silently overwriting the first one's
 * decision, so the dialog treats this as consequential rather than routine.
 */
export function ModerationPage(): React.JSX.Element {
  const [status, setStatus] = useState<ReportStatus | ''>('OPEN');
  const [subjectType, setSubjectType] = useState<ReportSubjectType | ''>('');
  const [resolving, setResolving] = useState<AdminReport | null>(null);

  const statusId = useId();
  const subjectId = useId();

  const page = useCursorPage<AdminReport>(RESOURCES.reports, {
    status: status || undefined,
    subjectType: subjectType || undefined,
    limit: 25,
  });

  const columns: Array<Column<AdminReport>> = [
    {
      key: 'createdAt',
      header: 'Filed',
      width: '11rem',
      sortValue: (row) => row.createdAt,
      render: (row) => <time dateTime={row.createdAt}>{formatDateTime(row.createdAt)}</time>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      sortValue: (row) => row.status,
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status.toLowerCase()}</Badge>,
    },
    {
      key: 'reason',
      header: 'Reason',
      sortValue: (row) => row.reason,
      render: (row) => (
        <span>
          <Mono>{row.reason}</Mono>
          {row.details ? (
            <span className="mt-1 block max-w-md truncate text-xs text-ink-soft">
              {row.details}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      sortValue: (row) => row.subjectType,
      render: (row) => <SubjectCell report={row} />,
    },
    {
      key: 'reporter',
      header: 'Reporter',
      hideBelow: 'lg',
      sortValue: (row) => row.reporter.lastName,
      render: (row) => (
        <Link to={`/users/${row.reporter.id}`} className="rounded hover:underline">
          <span className="block text-ink">
            {row.reporter.firstName} {row.reporter.lastName}
          </span>
          <span className="block text-xs text-ink-soft">{row.reporter.email}</span>
        </Link>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '7rem',
      render: (row) =>
        row.status === 'RESOLVED' || row.status === 'DISMISSED' ? (
          <span className="text-xs text-ink-faint">
            {row.resolvedAt ? formatDateTime(row.resolvedAt) : 'Closed'}
          </span>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setResolving(row)}>
            Resolve
          </Button>
        ),
    },
  ];

  return (
    <Page
      title="Moderation"
      description="Reports parents have filed, newest first."
      breadcrumbs={[{ label: 'People' }, { label: 'Moderation' }]}
      width="wide"
      action={
        <Button asChild variant="secondary">
          <Link to="/search-anomalies">
            <ShieldAlert aria-hidden="true" />
            Search anomalies
          </Link>
        </Button>
      }
    >
      <div className="space-y-5">
        <Notice title="Resolving a report cannot be undone">
          A report has one terminal decision. If someone else has already actioned it the server
          refuses yours rather than overwriting theirs, so refresh before you start a batch.
        </Notice>

        <div className="flex flex-wrap items-end gap-3 rounded-card border border-line bg-surface px-4 py-3">
          <div className="space-y-1.5">
            <Label htmlFor={statusId}>Status</Label>
            <NativeSelect
              id={statusId}
              className="w-44"
              value={status}
              onChange={(event) => setStatus(event.target.value as ReportStatus | '')}
            >
              <option value="">Any status</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value.charAt(0) + value.slice(1).toLowerCase()}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={subjectId}>Subject</Label>
            <NativeSelect
              id={subjectId}
              className="w-44"
              value={subjectType}
              onChange={(event) => setSubjectType(event.target.value as ReportSubjectType | '')}
            >
              <option value="">Any subject</option>
              {SUBJECT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {SUBJECT_LABELS[value]}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>

        <DataTable
          label="Reports"
          columns={columns}
          rows={page.rows}
          rowKey={(row) => row.id}
          isLoading={page.isLoading}
          error={page.error}
          onRetry={page.refetch}
          endpoint="GET /admin/reports"
          empty={
            <EmptyState
              title={status === 'OPEN' ? 'Nothing waiting' : 'No reports match that filter'}
              description={
                status === 'OPEN'
                  ? 'The open queue is clear. Widen the status filter to review what has already been actioned.'
                  : 'Try a different status or subject type.'
              }
              action={
                status !== '' ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setStatus('');
                      setSubjectType('');
                    }}
                  >
                    Show every report
                  </Button>
                ) : undefined
              }
            />
          }
          footer={
            <CursorFooter
              page={page}
              note="Subjects are shown by id. Names come from the audited reveal, never from this list."
            />
          }
        />
      </div>

      {resolving ? (
        <ResolveDialog
          report={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            setResolving(null);
            page.refetch();
          }}
        />
      ) : null}
    </Page>
  );
}

// ---------------------------------------------------------------------------

const SUBJECT_LABELS: Record<ReportSubjectType, string> = {
  USER: 'Parent account',
  CHILD: 'Child profile',
  WISHLIST_ITEM: 'Wishlist item',
};

/**
 * ⚠️ An id and a type — never a name, and never a photo.
 *
 * A `USER` subject links to the user screen, which is a parent and may be shown.
 * A `CHILD` subject deliberately does not link anywhere that would render a name
 * without a reason: the moderator follows the guardian instead, and reveals the
 * child there with a reason that lands in the audit log.
 */
function SubjectCell({ report }: { report: AdminReport }): React.JSX.Element {
  return (
    <span>
      <Badge tone={report.subjectType === 'CHILD' ? 'danger' : 'neutral'}>
        {SUBJECT_LABELS[report.subjectType]}
      </Badge>
      <span className="mt-1 block">
        {report.subjectType === 'USER' ? (
          <Link to={`/users/${report.subjectId}`} className="rounded hover:underline">
            <Mono>{report.subjectId}</Mono>
          </Link>
        ) : (
          <Mono>{report.subjectId}</Mono>
        )}
      </span>
    </span>
  );
}

function ResolveDialog({
  report,
  onClose,
  onResolved,
}: {
  report: AdminReport;
  onClose: () => void;
  onResolved: () => void;
}): React.JSX.Element {
  const [decision, setDecision] = useState<'RESOLVED' | 'DISMISSED'>('RESOLVED');
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decisionId = useId();
  const reasonId = useId();
  const request = useAdminRequest();

  const submit = async (): Promise<void> => {
    if (reason.trim().length < 5) {
      setError('Give a reason of at least 5 characters. It is the only record of this decision.');
      return;
    }

    setPending(true);
    setError(null);
    try {
      await request({
        url: `${RESOURCES.reports}/${report.id}/resolve`,
        method: 'post',
        payload: { status: decision, reason: reason.trim() },
      });
      notify.success(
        decision === 'RESOLVED' ? 'Resolved' : 'Dismissed',
        'The reason is in the audit log against this report.',
      );
      onResolved();
    } catch (err) {
      notify.error(err, 'The report was not closed');
    } finally {
      setPending(false);
    }
  };

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Resolve this report?"
      description="This is terminal — a report cannot be re-opened, and a second moderator's attempt will be refused rather than allowed to overwrite yours."
      confirmLabel="Resolve"
      loading={pending}
      onConfirm={submit}
    >
      <div className="space-y-4">
        <FormField
          label="Decision"
          htmlFor={decisionId}
          hint="Resolved when you took action. Dismissed when the report needed none."
          required
        >
          <NativeSelect
            value={decision}
            onChange={(event) => setDecision(event.target.value as 'RESOLVED' | 'DISMISSED')}
          >
            <option value="RESOLVED">Resolved — action taken</option>
            <option value="DISMISSED">Dismissed — no action needed</option>
          </NativeSelect>
        </FormField>

        <FormField
          label="Reason"
          htmlFor={reasonId}
          // ⚠️ Not a notes column: `reports` has none. The reason goes to
          // `audit_logs` and is read back with
          // `GET /admin/audit-logs?entityId=<reportId>`, which the Ops screen
          // does. Saying so here stops anyone hunting for it on the report row.
          hint="Recorded in the audit log against this report, not on the report row itself."
          error={error ?? undefined}
          required
        >
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Contacted the guardian; the item was removed."
          />
        </FormField>
      </div>
    </ConfirmDialog>
  );
}
